import React, { useState, useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  ShieldCheck,
  Check,
  X,
  Search,
  RotateCw,
  UserCheck,
  UserX,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
  Briefcase,
  Database,
} from "lucide-react";
import { UserProfile, UserRole } from "../../types";
import { useStaffManagementDirectory } from "../../services/staffService";
import { ExpandableCard } from "../common/ExpandableCard";

interface StaffSettingsViewProps {
  user: UserProfile | null;
}

type FilterTab = "all" | "rfp" | "not_involved" | "leads";

export const StaffSettingsView: React.FC<StaffSettingsViewProps> = ({ user: _user }) => {
  const {
    allStaff,
    activeRfpStaff,
    leads,
    loading,
    refreshing,
    error,
    refreshStaff,
    toggleAccess,
    changeRole,
    bulkUpdate,
  } = useStaffManagementDirectory();

  const [searchQuery, setSearchQuery] = useState("");
  const [filterTab, setFilterTab] = useState<FilterTab>("all");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [bulkRole, setBulkRole] = useState<UserRole>("lead");
  const [isProcessingBulk, setIsProcessingBulk] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  const showToast = (message: string) => {
    setToastMessage(message);
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = setTimeout(() => {
      setToastMessage(null);
    }, 2500);
  };

  // Filter staff by search, active tab, and role
  const filteredStaff = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return allStaff.filter((member) => {
      // Tab filter
      if (filterTab === "rfp" && !member.hasAccess) return false;
      if (filterTab === "not_involved" && member.hasAccess) return false;
      if (
        filterTab === "leads" &&
        (!member.hasAccess || (member.role !== "admin" && member.role !== "lead"))
      ) {
        return false;
      }

      // Role filter
      if (roleFilter !== "all" && member.role !== roleFilter) return false;

      // Text search
      if (!query) return true;

      const nameMatch = member.displayName.toLowerCase().includes(query);
      const emailMatch = member.email.toLowerCase().includes(query);
      const deptMatch = (member.department || "").toLowerCase().includes(query);
      const titleMatch = (member.title || "").toLowerCase().includes(query);

      return nameMatch || emailMatch || deptMatch || titleMatch;
    });
  }, [allStaff, searchQuery, filterTab, roleFilter]);

  // Checkbox handlers
  const isAllSelected =
    filteredStaff.length > 0 &&
    filteredStaff.every((m) => selectedEmails.includes(m.email.toLowerCase()));

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedEmails([]);
    } else {
      setSelectedEmails(filteredStaff.map((m) => m.email.toLowerCase()));
    }
  };

  const toggleSelectMember = (email: string) => {
    const normalized = email.toLowerCase();
    setSelectedEmails((prev) =>
      prev.includes(normalized) ? prev.filter((e) => e !== normalized) : [...prev, normalized],
    );
  };

  // Single-action handlers
  const handleToggleAccess = async (email: string, currentAccess: boolean) => {
    const newAccess = !currentAccess;
    try {
      await toggleAccess(email, newAccess);
      showToast(newAccess ? `Granted RFP access to ${email}` : `Revoked RFP access from ${email}`);
    } catch (err) {
      console.error("Failed to toggle access:", err);
      showToast("Error updating access.");
    }
  };

  const handleRoleChange = async (email: string, newRole: UserRole) => {
    try {
      await changeRole(email, newRole);
      showToast(`Updated role for ${email} to ${newRole}`);
    } catch (err) {
      console.error("Failed to change role:", err);
      showToast("Error updating role.");
    }
  };

  // Bulk action handlers
  const handleBulkGrant = async () => {
    if (selectedEmails.length === 0) return;
    setIsProcessingBulk(true);
    try {
      await bulkUpdate(selectedEmails, { hasAccess: true });
      showToast(`Granted RFP access to ${selectedEmails.length} staff members.`);
      setSelectedEmails([]);
    } catch (err) {
      console.error("Failed bulk grant:", err);
      showToast("Failed to perform bulk grant.");
    } finally {
      setIsProcessingBulk(false);
    }
  };

  const handleBulkRevoke = async () => {
    if (selectedEmails.length === 0) return;
    setIsProcessingBulk(true);
    try {
      await bulkUpdate(selectedEmails, { hasAccess: false });
      showToast(`Revoked RFP access from ${selectedEmails.length} staff members.`);
      setSelectedEmails([]);
    } catch (err) {
      console.error("Failed bulk revoke:", err);
      showToast("Failed to perform bulk revoke.");
    } finally {
      setIsProcessingBulk(false);
    }
  };

  const handleBulkRoleChange = async () => {
    if (selectedEmails.length === 0) return;
    setIsProcessingBulk(true);
    try {
      await bulkUpdate(selectedEmails, { role: bulkRole });
      showToast(`Assigned role "${bulkRole}" to ${selectedEmails.length} staff members.`);
      setSelectedEmails([]);
    } catch (err) {
      console.error("Failed bulk role update:", err);
      showToast("Failed to perform bulk role update.");
    } finally {
      setIsProcessingBulk(false);
    }
  };

  const notInvolvedCount = allStaff.length - activeRfpStaff.length;

  return (
    <div className="space-y-6">
      {/* Top Breadcrumb & Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
          <Link
            to="/settings"
            className="inline-flex items-center gap-1 hover:text-slate-900 dark:hover:text-slate-100"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span>Settings</span>
          </Link>
          <span>/</span>
          <span className="font-semibold text-slate-800 dark:text-slate-200">
            Staff Access & Roles
          </span>
        </div>

        {/* Sync from Staff HQ button */}
        <button
          type="button"
          onClick={() => refreshStaff()}
          disabled={refreshing}
          className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-xs hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 disabled:opacity-50"
        >
          <RotateCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin text-[#2ba5b5]" : ""}`} />
          <span>{refreshing ? "Syncing..." : "Sync from Staff HQ"}</span>
        </button>
      </div>

      {/* Page Title & Context */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            Staff Users Access & Role Management
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Synchronized with MLC Staff HQ. Choose which team members are involved in the RFP
            process, control login authorization, and assign proposal permissions.
          </p>
        </div>

        {toastMessage && (
          <div className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
            <CheckCircle2 className="h-4 w-4" />
            <span>{toastMessage}</span>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-rose-50 p-3 text-xs text-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Staff HQ Integration & Synchronization Details Card */}
      <ExpandableCard
        title="Staff HQ Integration & Synchronization Details"
        badge="mlc-hr-tools-db"
        badgeColor="bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300"
        summary={
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-[#2ba5b5]" />
            <span>
              Connected to MLC Staff HQ (<code className="font-mono text-xs">mlc-hr-tools-db</code>
              ). Click the sync button above to pull active company staff into Proposal HQ.
            </span>
          </div>
        }
        defaultExpanded={false}
        details={
          <div className="space-y-3 text-xs">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-2.5 dark:border-slate-800 dark:bg-slate-900/50">
                <span className="text-[11px] font-semibold uppercase text-slate-400">
                  Source Database
                </span>
                <p className="mt-1 font-mono font-bold text-slate-800 dark:text-slate-200">
                  mlc-hr-tools-db
                </p>
                <p className="text-[10px] text-slate-400">MLC Staff HQ named database</p>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-2.5 dark:border-slate-800 dark:bg-slate-900/50">
                <span className="text-[11px] font-semibold uppercase text-slate-400">
                  Sync Mechanism
                </span>
                <p className="mt-1 font-mono font-bold text-[#2ba5b5]">phq_syncFromHr</p>
                <p className="text-[10px] text-slate-400">2nd Gen Cloud Function</p>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-2.5 dark:border-slate-800 dark:bg-slate-900/50">
                <span className="text-[11px] font-semibold uppercase text-slate-400">
                  Domain Filter
                </span>
                <p className="mt-1 font-medium text-slate-800 dark:text-slate-200">
                  @mlconnections.com
                </p>
                <p className="text-[10px] text-slate-400">Restricted to corporate staff</p>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-2.5 dark:border-slate-800 dark:bg-slate-900/50">
                <span className="text-[11px] font-semibold uppercase text-slate-400">
                  Removal Policy
                </span>
                <p className="mt-1 font-medium text-emerald-600 dark:text-emerald-400">
                  Auto-Exclusion
                </p>
                <p className="text-[10px] text-slate-400">Departed staff removed on sync</p>
              </div>
            </div>

            <div className="rounded-lg bg-teal-50/60 p-3 text-slate-700 dark:bg-teal-950/30 dark:text-slate-300">
              <p className="font-semibold text-[#238997] dark:text-teal-300">
                How Staff Synchronization & Access Work:
              </p>
              <ul className="mt-1.5 list-disc space-y-1 pl-4 text-[11px] text-slate-600 dark:text-slate-400">
                <li>
                  <strong>Pulling Other People:</strong> Click the{" "}
                  <em>&quot;Sync from Staff HQ&quot;</em> button in the header. The Cloud Function
                  queries <code className="font-mono text-xs">mlc-hr-tools-db</code> and loads all
                  active company employees into the directory.
                </li>
                <li>
                  <strong>Granting Proposal Access:</strong> Switch to the{" "}
                  <em>&quot;All Staff&quot;</em> or <em>&quot;Not Involved&quot;</em> tab below,
                  locate the employee, and toggle their RFP Access switch to{" "}
                  <em>&quot;Granted&quot;</em>. They will immediately become selectable across
                  proposals, SME task assignments, and review queues.
                </li>
                <li>
                  <strong>Automatic Removals:</strong> When an employee is removed or set to
                  inactive/terminated in Staff HQ, they are automatically excluded from Proposal HQ
                  on the next sync and will no longer appear in new proposal assignee dropdowns. In
                  addition, the Answer Freshness scanner will flag any existing library answers or
                  draft sections owned by departed staff for review and reassignment.
                </li>
              </ul>
            </div>
          </div>
        }
      />

      {/* KPI Summary Cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">
              Staff HQ Synced
            </span>
            <Users className="h-4 w-4 text-slate-400" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-900 dark:text-white">
              {allStaff.length}
            </span>
            <span className="text-[11px] text-slate-400">Employees</span>
          </div>
        </div>

        <div className="rounded-xl border border-teal-200/80 bg-teal-50/50 p-4 shadow-xs dark:border-teal-900/60 dark:bg-teal-950/20">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase text-[#238997] dark:text-teal-400">
              Authorized RFP Team
            </span>
            <UserCheck className="h-4 w-4 text-[#2ba5b5]" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-900 dark:text-white">
              {activeRfpStaff.length}
            </span>
            <span className="text-[11px] text-teal-600 dark:text-teal-400">
              Involved in proposals
            </span>
          </div>
        </div>

        <div className="rounded-xl border border-indigo-200/80 bg-indigo-50/50 p-4 shadow-xs dark:border-indigo-900/60 dark:bg-indigo-950/20">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase text-indigo-700 dark:text-indigo-400">
              Admins & Leads
            </span>
            <ShieldCheck className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-900 dark:text-white">
              {leads.length}
            </span>
            <span className="text-[11px] text-indigo-600 dark:text-indigo-400">
              Proposal Owners / Deciders
            </span>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">
              Not Involved
            </span>
            <UserX className="h-4 w-4 text-slate-400" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-900 dark:text-white">
              {notInvolvedCount}
            </span>
            <span className="text-[11px] text-slate-400">Company staff</span>
          </div>
        </div>
      </div>

      {/* Filter Tabs & Search Controls */}
      <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-xs dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Filter Pills */}
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={() => setFilterTab("all")}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
                filterTab === "all"
                  ? "bg-[#2ba5b5] text-white shadow-xs"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
              }`}
            >
              All Staff ({allStaff.length})
            </button>
            <button
              type="button"
              onClick={() => setFilterTab("rfp")}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
                filterTab === "rfp"
                  ? "bg-[#2ba5b5] text-white shadow-xs"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
              }`}
            >
              RFP Team Only ({activeRfpStaff.length})
            </button>
            <button
              type="button"
              onClick={() => setFilterTab("leads")}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
                filterTab === "leads"
                  ? "bg-[#2ba5b5] text-white shadow-xs"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
              }`}
            >
              Admins & Leads ({leads.length})
            </button>
            <button
              type="button"
              onClick={() => setFilterTab("not_involved")}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
                filterTab === "not_involved"
                  ? "bg-[#2ba5b5] text-white shadow-xs"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
              }`}
            >
              Not Involved ({notInvolvedCount})
            </button>
          </div>

          {/* Role Filter Dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 dark:text-slate-400">Role:</span>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-800 outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              <option value="all">All Roles</option>
              <option value="admin">Administrator</option>
              <option value="lead">Proposal Lead</option>
              <option value="contributor">Contributor / SME</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
        </div>

        {/* Search Input Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search staff by name, email, department, or job title..."
            className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-4 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] focus:bg-white dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-[#2ba5b5]"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Floating Bulk Actions Bar (Visible when 1+ staff are selected) */}
      {selectedEmails.length > 0 && (
        <div className="sticky top-20 z-20 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-teal-200 bg-white/95 p-3 shadow-lg backdrop-blur-md dark:border-teal-900 dark:bg-slate-900/95">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-6 items-center justify-center rounded-full bg-[#2ba5b5] px-2 text-xs font-bold text-white">
              {selectedEmails.length}
            </span>
            <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">
              Staff selected
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Bulk Grant */}
            <button
              type="button"
              onClick={handleBulkGrant}
              disabled={isProcessingBulk}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-emerald-700 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" />
              <span>Grant RFP Access</span>
            </button>

            {/* Bulk Revoke */}
            <button
              type="button"
              onClick={handleBulkRevoke}
              disabled={isProcessingBulk}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
              <span>Revoke Access</span>
            </button>

            {/* Bulk Role Assign */}
            <div className="flex items-center gap-1">
              <select
                value={bulkRole}
                onChange={(e) => setBulkRole(e.target.value as UserRole)}
                className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-800 outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                <option value="lead">Proposal Lead</option>
                <option value="contributor">Contributor / SME</option>
                <option value="viewer">Viewer</option>
                <option value="admin">Administrator</option>
              </select>
              <button
                type="button"
                onClick={handleBulkRoleChange}
                disabled={isProcessingBulk}
                className="btn-animate inline-flex items-center gap-1 rounded-lg bg-[#2ba5b5] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#238997] disabled:opacity-50"
              >
                <span>Apply Role</span>
              </button>
            </div>

            {/* Clear selection */}
            <button
              type="button"
              onClick={() => setSelectedEmails([])}
              className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            >
              Deselect All
            </button>
          </div>
        </div>
      )}

      {/* Staff Management Table */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xs dark:border-slate-800 dark:bg-slate-900">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="border-b border-slate-200 bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400">
              <tr>
                <th className="w-10 px-4 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all staff"
                    className="rounded border-slate-300 text-[#2ba5b5] focus:ring-[#2ba5b5]"
                  />
                </th>
                <th className="px-4 py-3">Staff Member</th>
                <th className="px-4 py-3">Department / Title</th>
                <th className="px-4 py-3">RFP Access</th>
                <th className="px-4 py-3">Proposal Role</th>
                <th className="px-4 py-3 text-right">Quick Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-400">
                    Loading staff directory from Staff HQ...
                  </td>
                </tr>
              ) : filteredStaff.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-400">
                    No staff members match the selected filters or search query.
                  </td>
                </tr>
              ) : (
                filteredStaff.map((member) => {
                  const isSelected = selectedEmails.includes(member.email.toLowerCase());
                  const hasAccess = member.hasAccess === true;
                  const role = member.role || "contributor";

                  return (
                    <tr
                      key={member.id || member.email}
                      className={`transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40 ${
                        isSelected ? "bg-teal-50/40 dark:bg-teal-950/20" : ""
                      }`}
                    >
                      {/* Checkbox */}
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelectMember(member.email)}
                          aria-label={`Select ${member.displayName}`}
                          className="rounded border-slate-300 text-[#2ba5b5] focus:ring-[#2ba5b5]"
                        />
                      </td>

                      {/* Staff Member Info */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {member.photoURL ? (
                            <img
                              src={member.photoURL}
                              alt={member.displayName}
                              className="h-8 w-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2ba5b5] text-xs font-bold text-white">
                              {member.displayName.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 dark:text-slate-100">
                              {member.displayName}
                            </p>
                            <p className="font-mono text-[11px] text-slate-400">{member.email}</p>
                          </div>
                        </div>
                      </td>

                      {/* Department / Title */}
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-0.5">
                          {member.department && (
                            <span className="inline-flex items-center gap-1 font-medium text-slate-700 dark:text-slate-300">
                              <Briefcase className="h-3 w-3 text-slate-400" />
                              <span>{member.department}</span>
                            </span>
                          )}
                          {member.title && (
                            <span className="text-[11px] text-slate-400">{member.title}</span>
                          )}
                          {!member.department && !member.title && (
                            <span className="text-[11px] text-slate-400">Staff Member</span>
                          )}
                        </div>
                      </td>

                      {/* RFP Access Status & Toggle */}
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => handleToggleAccess(member.email, hasAccess)}
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-all ${
                            hasAccess
                              ? "bg-teal-100 text-[#238997] hover:bg-teal-200 dark:bg-teal-950/60 dark:text-teal-300"
                              : "bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400"
                          }`}
                        >
                          {hasAccess ? (
                            <>
                              <Check className="h-3 w-3" />
                              <span>Access Granted</span>
                            </>
                          ) : (
                            <>
                              <X className="h-3 w-3" />
                              <span>Not Involved</span>
                            </>
                          )}
                        </button>
                      </td>

                      {/* Proposal Role Selector */}
                      <td className="px-4 py-3">
                        <select
                          value={role}
                          onChange={(e) =>
                            handleRoleChange(member.email, e.target.value as UserRole)
                          }
                          disabled={!hasAccess}
                          aria-label={`Role for ${member.displayName}`}
                          className={`rounded-lg border px-2 py-1 text-xs font-medium outline-hidden disabled:opacity-50 ${
                            role === "admin"
                              ? "border-purple-200 bg-purple-50 text-purple-800 dark:border-purple-800 dark:bg-purple-950/40 dark:text-purple-300"
                              : role === "lead"
                                ? "border-teal-200 bg-teal-50 text-[#2ba5b5] font-semibold dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-300"
                                : role === "contributor"
                                  ? "border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300"
                                  : "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                          }`}
                        >
                          <option value="admin">Administrator</option>
                          <option value="lead">Proposal Lead</option>
                          <option value="contributor">Contributor / SME</option>
                          <option value="viewer">Viewer (Read-only)</option>
                        </select>
                      </td>

                      {/* Quick Action */}
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => handleToggleAccess(member.email, hasAccess)}
                          className="btn-animate rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                          title={hasAccess ? "Revoke RFP Access" : "Grant RFP Access"}
                          aria-label={
                            hasAccess
                              ? `Revoke access for ${member.displayName}`
                              : `Grant access for ${member.displayName}`
                          }
                        >
                          {hasAccess ? (
                            <UserX className="h-4 w-4 text-rose-500" />
                          ) : (
                            <UserCheck className="h-4 w-4 text-teal-600" />
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
